// ink-component
import React, { ReactNode, MouseEvent } from 'react';

import Block, { PrimaryActionType, SecondaryActionType, TertiaryActionType } from '../Block/Block';
import { withModalContext } from '../../contexts/ModalContext';

import { bemHelper } from '../../helpers';
import { IconTypes } from '../../helpers/constants';

import getGlobalProps from '../../helpers/constants/global-props';
import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';

export interface ModalContentProps {
  /** Title */
  title?: string;
  /** Content */
  children?: any;
  /** Header icon left type. You can verify the available icons [here](/components/Icon/usage). */
  iconLeftType?: IconTypes;
  /** Header icon left callback */
  iconLeftCallback?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Header icon right type.
   * You can verify the available icons [here](/components/Icon/usage). */
  iconRightType?: IconTypes;
  /** Header icon right callback */
  iconRightCallback?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Defines the width of the modal */
  width?: 'mini' | 'small' | 'medium' | 'large';
  /** Primary action configuration object. Pass it as the only action for single-action behavior. */
  primaryAction?: PrimaryActionType;
  /** Secondary action configuration object. */
  secondaryAction?: SecondaryActionType;
  /** Tertiary action configuration object. */
  tertiaryAction?: TertiaryActionType;
  /** Disables all footer buttons while async processes are running */
  actionIsLoading: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Allows screen readers to describe the content */
  'aria-label'?: string;

  /** @ignore */
  isLoading?: boolean;
  /** @ignore */
  hideModal?: () => void;
  /** @ignore */
  actionText?: ReactNode;
  /** @ignore */
  actionType?: 'danger' | 'primary';
  /** @ignore */
  actionCallback?: (nextStep?: () => void) => Record<string, unknown>;
}

const globalProps = getGlobalProps({ deprecated: true, inkSass: true });

/**
 * @categories layout
 * @purpose Separates parts of complex workflows inside blocks that overlay the page
 * @deprecated This component is deprecated and replaced by [Modal](https://ink.carta.com/components/Modal/)
 * @status deprecated
 * @replacementComponent Modal
 * @dateOfDeletion September 1st, 2023
 */
function ModalContent(props: ModalContentProps) {
  useDeprecatedWarning({ componentName: 'ModalContent', substitute: 'Modal' });

  const { 'data-testid': dataTestId, 'aria-label': ariaLabel, ...otherProps } = props;
  const modalHide = e => {
    props.iconRightCallback && props.iconRightCallback(e);
    props.hideModal && props.hideModal();
  };

  const defineContentClassName = () => {
    const { width } = props;
    const b = bemHelper();

    return b('ink-modal--content', {
      [`${width}`]: Boolean(width),
    });
  };

  return (
    <div
      className={defineContentClassName()}
      role="dialog"
      data-testid={props['data-testid']}
      aria-label={props.title ? props.title : ariaLabel}
      {...globalProps}
    >
      <Block {...otherProps} isModal actionSize="full" iconRightCallback={modalHide}>
        {props.children}
      </Block>
    </div>
  );
}

ModalContent.defaultProps = {
  title: undefined,
  children: undefined,
  actionIsLoading: false,
  primaryAction: {
    type: 'primary',
    callback: () => {},
  },
  secondaryAction: {
    callback: () => {},
  },
  tertiaryAction: {
    type: 'transparent',
    callback: () => {},
  },
  iconLeftType: undefined,
  iconRightType: 'close',
  iconLeftCallback: () => {},
  iconRightCallback: () => {},
  width: 'medium',
  'data-testid': undefined,
  'aria-label': undefined,

  isLoading: false,
  actionText: undefined,
  actionType: undefined,
  actionCallback: undefined,
};

ModalContent.displayName = 'ModalContent';

export { ModalContent as ModalContentDefault };

export default withModalContext(ModalContent);
